// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2024 Vic Zhu <viczhu2014@gmail.com>

import { useMemo } from 'react'
import { useGetList, useGetOne } from 'react-admin'
import { DISLODGEMENT_DETECTION } from '../common'
import { isVirtualDevice } from '../common/virtualDevice'
import { Device, Metric } from '../types'
import { isValidDeviceId } from '../utils/deviceValidation'
import {
  DislodgementRange,
  filterDeviceDataWithRanges,
  filterDevicesDataWithRanges,
} from '../utils/glucoseFiltering'
import { calculateMetricsPagination, getDefaultMetricsPagination } from '../utils/paginationUtils'
import { usePetDevicesWithHistory } from './usePetDevicesWithHistory'

// Dislodgement ranges already logged this session (see the dedup note at the
// log site) — module-level so every consumer of the hook shares it.
const loggedDislodgementRanges = new Set<string>()

interface UseDeviceMetricsOptions {
  deviceId?: string | number
  petId?: string | number
  startDate?: Date
  endDate?: Date
  sort?: { field: string; order: 'ASC' | 'DESC' }
  pagination?: { page: number; perPage: number }
  /**
   * With a virtual "All Devices" id: restrict the pet's devices to this subset
   * (vic/vetpo#56 — a report exported for the sensors the vet ticked). The
   * multi-device overlap resolution then runs among the SELECTED devices only,
   * so an unticked sensor can never win an overlap and drop a ticked one's
   * readings. Ignored for a single concrete deviceId. An empty array selects
   * nothing (no fetch), never "all".
   */
  deviceIds?: ReadonlyArray<string | number>
  /**
   * Also return each device's readings filtered ON ITS OWN (warm-up +
   * dislodgement), WITHOUT the multi-device overlap resolution
   * (vic/vetpo#56: one PDF trend chart per sensor — where a replacement went
   * on before the old sensor came off, the overlap hours belong in BOTH
   * sensors' charts). `data` is unchanged either way.
   */
  perDevice?: boolean
}

interface DeviceMetricsResult {
  data: Metric[]
  total: number
  isLoading: boolean
  /**
   * A device-fetch error (petDevices for virtual "all devices", or the single
   * device record) OR a metrics-fetch error. Device errors take precedence —
   * they are surfaced even if the metrics query never ran or came back clean,
   * so a failed device fetch is never indistinguishable from "this pet has no
   * device".
   */
  error: any
  isVirtualDevice: boolean
  deviceIds: (string | number)[]
  devices: Device[]
  /** Time ranges (Unix seconds) hidden due to suspected sensor dislodgement */
  hiddenRanges: DislodgementRange[]
  /**
   * True when the server holds more rows matching the query than it returned
   * (the /metrics endpoint silently clamps `limit`, and perPage caps bind).
   * Consumers presenting "all data in a range" (PDF/CSV exports, statistics)
   * must surface this — silent truncation reads as complete clinical data.
   */
  isTruncated: boolean
  /**
   * When truncated: the earliest RAW reading the server returned. The query is
   * newest-first, so the cap drops the OLDEST rows — nothing before this was
   * fetched (eng-brain P69-6). null when not truncated. Memoized: the same
   * instant keeps the same Date, so it is safe in a dependency array.
   */
  truncatedBefore: Date | null
  /** With `perDevice`: device id (as a string) → that device's readings, ASC. */
  perDeviceData: Map<string, Metric[]>
}

/**
 * Unified hook for fetching metrics from a single device or all devices for a pet/owner
 * Handles virtual "All Devices" selection transparently
 */
export function useDeviceMetrics({
  deviceId,
  petId,
  startDate,
  endDate,
  sort = { field: 'timestamp', order: 'ASC' },
  pagination, // Will be calculated dynamically if not provided
  deviceIds,
  perDevice = false,
}: UseDeviceMetricsOptions): DeviceMetricsResult {
  // Calculate dynamic pagination based on date range
  const dynamicPerPage = useMemo(() => {
    if (pagination?.perPage) {
      // Use explicitly provided pagination
      return pagination.perPage
    }

    if (startDate && endDate) {
      // Calculate based on date range
      return calculateMetricsPagination(startDate, endDate)
    }

    // Default to 30 days worth of data
    return getDefaultMetricsPagination()
  }, [pagination?.perPage, startDate, endDate])

  const isVirtual = isVirtualDevice(deviceId)

  // Fetch all devices for the pet if using pet-level virtual device
  const {
    data: petDevices = [],
    isLoading: petDevicesLoading,
    error: petDevicesError,
  } = usePetDevicesWithHistory(isVirtual && petId ? petId : null)

  // Fetch single device if using specific device ID (non-virtual)
  const singleDeviceId = !isVirtual && deviceId && isValidDeviceId(deviceId) ? deviceId : undefined
  const {
    data: singleDevice,
    isLoading: singleDeviceLoading,
    error: singleDeviceError,
  } = useGetOne<Device>('devices', { id: singleDeviceId as number }, { enabled: !!singleDeviceId })

  // Stable key for the subset so a fresh array literal per render does not
  // churn the memo below (S4-class query-key churn).
  const deviceIdsKey = deviceIds ? deviceIds.map(String).sort().join(',') : null

  // Get the devices we're working with
  const allDevices = useMemo((): Device[] => {
    if (isVirtual && petDevices.length > 0) {
      if (deviceIdsKey === null) return petDevices as Device[]
      const wanted = new Set(deviceIdsKey ? deviceIdsKey.split(',') : [])
      return (petDevices as Device[]).filter((d) => wanted.has(String(d.id)))
    }
    // For single device query, return the single device if we have it
    if (singleDevice) {
      return [singleDevice]
    }
    return []
  }, [isVirtual, petDevices, singleDevice, deviceIdsKey])

  // Scale pagination by device count for virtual devices (multiple CGM sensors)
  const deviceCount = allDevices.length || 1
  const effectivePagination = {
    page: pagination?.page || 1,
    perPage: Math.min(dynamicPerPage * deviceCount, 50000),
  }

  // Cross-pet device guard: `selectedDeviceId` is app-global, and chart
  // components fall back to it via `propDeviceId ?? storeDeviceId`, so a
  // STALE prior-patient device id can arrive here alongside the new pet's
  // petId. When both a non-virtual deviceId AND a petId are supplied, the
  // device record must CONFIRM the device belongs to that pet before any
  // metrics are fetched — fetching during the confirmation window painted
  // the other patient's glucose from a warm react-query cache (cross-pet
  // leak class, PROD 2026-06). Device-centric callers (no petId) keep the
  // immediate-fetch behavior.
  const requestedPetId =
    petId !== undefined && petId !== null && petId !== '' && Number.isFinite(Number(petId))
      ? Number(petId)
      : null
  const singleDeviceMatchesPet = useMemo(() => {
    if (!singleDeviceId || requestedPetId === null) return true // no cross-check requested
    if (!singleDevice) return false // unconfirmed while loading → do not fetch yet
    const rawPet = singleDevice.pet
    const devicePetId =
      typeof rawPet === 'object' && rawPet !== null ? Number(rawPet.id) : Number(rawPet)
    return devicePetId === requestedPetId
  }, [singleDeviceId, requestedPetId, singleDevice])

  // Devices visible to the rest of the pipeline — empty while the cross-pet
  // gate is closed, so stale-device rows can't flow through even from a warm
  // react-query cache (defense-in-depth beyond disabling the fetch).
  const gatedDevices = useMemo(
    () => (singleDeviceMatchesPet ? allDevices : []),
    [singleDeviceMatchesPet, allDevices],
  )

  // Get actual device IDs to query
  const actualDeviceIds = useMemo(() => {
    if (!singleDeviceMatchesPet) return []
    if (gatedDevices.length > 0) {
      return gatedDevices.map((d) => d.id)
    }
    return deviceId ? [deviceId] : []
  }, [gatedDevices, deviceId, singleDeviceMatchesPet])

  // Get only numerically valid device IDs (excludes virtual IDs like "all-devices-pet-123")
  const validDeviceIds = useMemo(
    () => actualDeviceIds.filter((id) => isValidDeviceId(id)),
    [actualDeviceIds],
  )

  // Build the metrics filter INLINE (not via useMemo) so it is ALWAYS consistent
  // with `validDeviceIds` in the same render. A memoized filter could lag
  // `validDeviceIds` by a render (the allDevices ← usePetDevicesWithHistory ref
  // chain churns as its queries settle): the length-based `enabled` below then
  // read "has devices = true" while the stale filter still had NO device key, so
  // react-query fired an UNFILTERED metrics request (up to 50k rows across all of
  // the vet's patients) for every consumer. react-query hashes the query key by
  // VALUE, so rebuilding this object each render does not cause extra refetches —
  // only a genuine id/date change does.
  // IMPORTANT: we filter by device.start_time (sensor activation) NOT pet.createdAt
  // (PROD-1111 — pet.createdAt resets on phone switch, losing device history).
  const filter: Record<string, any> = {}
  if (validDeviceIds.length === 1) {
    filter.device = validDeviceIds[0]
  } else if (validDeviceIds.length > 1) {
    validDeviceIds.forEach((id, index) => {
      filter[`device[in][${index}]`] = id
    })
  }
  if (startDate) {
    filter.timestamp_gte = Math.floor(startDate.getTime() / 1000)
  }
  if (endDate) {
    filter.timestamp_lte = Math.floor(endDate.getTime() / 1000)
  }

  // Don't fetch until at least one valid device ID is present. Because `filter`
  // is now built inline from the SAME `validDeviceIds`, this guard and the
  // filter can never disagree, so the query never runs without a device.
  const hasValidDevices = validDeviceIds.length > 0

  // Fetch metrics — ALWAYS query newest-first, regardless of the caller's
  // requested `sort`. The /metrics endpoint SILENTLY clamps `limit` to
  // METRICS_MAX_LIMIT (20000 default; ada-core Metrics/index.ts, raised
  // 5000→20000 on 2026-06-29 after the 5000 cap re-froze ASC charts ~10 days
  // in — originally added 2026-06-27 to stop multi-50k-row queries from
  // exhausting DB connections). With an ASC query the cap returns the OLDEST
  // rows up to the limit — and since the oldest rows never change as new data
  // arrives, any pet with >20000 readings (~41.7 days at 3-min intervals) would
  // have its chart PERMANENTLY frozen at sensor_start + ~42d (recent data
  // silently dropped). Querying DESC makes the cap retain the most recent rows
  // (a rolling window that advances daily); `sortedMetrics` below re-sorts to
  // the caller's requested order, so the returned contract is unchanged.
  // Long-range "ALL" history beyond the cap still needs date-windowed fetching
  // / downsampling — tracked separately; this only un-freezes charts.
  const querySort = { field: sort.field, order: 'DESC' as const }
  const {
    data: metrics = [],
    total = 0,
    isLoading: metricsLoading,
    error: metricsError,
  } = useGetList<Metric>(
    'metrics',
    {
      filter,
      sort: querySort,
      pagination: effectivePagination,
      // depth 0: at Payload's default depth every reading embedded the whole
      // device record (1,005 of 1,351 bytes -- ~8 MB per median patient view,
      // eng-brain P73-1). Consumers need only the id; the CSV export resolves
      // device names from `devices` below. Same meta for every consumer, so
      // the shared query entries still dedupe.
      meta: { params: { depth: 0 } },
    },
    { enabled: hasValidDevices },
  )

  // Combine loading states
  const isLoading = isVirtual
    ? petDevicesLoading || metricsLoading
    : metricsLoading || singleDeviceLoading

  // Sort metrics by timestamp to ensure chronological order across devices
  const sortedMetrics = useMemo(() => {
    if (!metrics || metrics.length === 0) return []

    // Create a copy and sort. Tie-break equal timestamps by id so duplicate
    // readings resolve deterministically regardless of fetch direction.
    return [...metrics].sort((a, b) => {
      const byTime = sort.order === 'ASC' ? a.timestamp - b.timestamp : b.timestamp - a.timestamp
      return byTime !== 0 ? byTime : Number(a.id) - Number(b.id)
    })
  }, [metrics, sort.order])

  // Apply comprehensive data filtering:
  // 1. Warm-up period: Remove data during 1-hour warm-up after device activation
  // 2. 14-day window: Only include data within 14 days from device activation
  // 3. Dislodgement detection: Remove data after device dislodgement is detected
  //    (when consecutive readings show minimum glucose value)
  const { filteredMetrics, hiddenRanges } = useMemo(() => {
    if (sortedMetrics.length === 0 || gatedDevices.length === 0) {
      // Return empty array — don't expose unfiltered data while devices are loading.
      // Without device info we can't apply warmup/overlap/dislodgement filters,
      // and showing raw data causes bogus timestamps to pollute chart ranges.
      return { filteredMetrics: [] as Metric[], hiddenRanges: [] as DislodgementRange[] }
    }

    // Single source of truth for both the hidden data and the chart overlay,
    // so every trend chart that consumes this hook hides suspected-dislodgement
    // data identically — no per-chart divergence. Single-device keeps the
    // lenient warm-up behavior (readings retained when start_time is unknown);
    // multi-device applies overlap resolution and strict warm-up.
    const { filtered, ranges } =
      gatedDevices.length === 1
        ? filterDeviceDataWithRanges(sortedMetrics, gatedDevices[0].start_time, gatedDevices)
        : filterDevicesDataWithRanges(sortedMetrics, gatedDevices)
    return { filteredMetrics: filtered, hiddenRanges: ranges }
  }, [sortedMetrics, gatedDevices])

  // Per-device series (see `perDevice`): each device filtered alone, so the
  // overlap hours a replacement sensor shares with the old one stay in both.
  const perDeviceData = useMemo(() => {
    const byDevice = new Map<string, Metric[]>()
    if (!perDevice || sortedMetrics.length === 0) return byDevice
    for (const device of gatedDevices) {
      const own = sortedMetrics.filter((m) => {
        const id = typeof m.device === 'object' && m.device !== null ? m.device.id : m.device
        return String(id) === String(device.id)
      })
      byDevice.set(String(device.id), filterDevicesDataWithRanges(own, [device]).filtered)
    }
    return byDevice
  }, [perDevice, sortedMetrics, gatedDevices])

  // Debug logging for support troubleshooting: why/when/what-range/device.
  // Limitation: detection only catches readings clipped to the sensor minimum;
  // a sensor that flatlines at a non-minimum low value is NOT hidden here.
  // Deduped per session: many components share this hook and `hiddenRanges`
  // gets a fresh identity on every recompute, so an un-deduped log printed the
  // SAME range hundreds of times under render churn (2026-07-10, pet 68238) —
  // noise that buried real errors during support debugging.
  useMemo(() => {
    if (hiddenRanges.length === 0) return
    for (const range of hiddenRanges) {
      const key = `${petId}|${range.deviceId}|${range.start}|${range.end}|${range.reason}`
      if (loggedDislodgementRanges.has(key)) continue
      loggedDislodgementRanges.add(key)
      console.info('[dislodgement] hidden range', {
        petId,
        deviceId: range.deviceId,
        start: range.start,
        end: range.end,
        reason: range.reason,
        algoVersion: DISLODGEMENT_DETECTION.algoVersion,
      })
    }
  }, [hiddenRanges, petId])

  // Compare RAW rows against the server's (unclamped) totalDocs — the
  // dislodgement/warm-up filters legitimately shrink the returned set, so the
  // filtered length must not be used here.
  const isTruncated = total > metrics.length
  const truncatedBeforeSec =
    isTruncated && metrics.length > 0
      ? metrics.reduce(
          (min, m) => (typeof m.timestamp === 'number' && m.timestamp < min ? m.timestamp : min),
          Number.POSITIVE_INFINITY,
        )
      : null
  const truncatedBefore = useMemo(
    () =>
      truncatedBeforeSec != null && Number.isFinite(truncatedBeforeSec)
        ? new Date(truncatedBeforeSec * 1000)
        : null,
    [truncatedBeforeSec],
  )

  // Honor the caller's requested order on the FINAL output. The filter stage
  // above always returns ASC (it re-sorts internally), so before this a DESC
  // caller silently received ASC and `data[0]` was the OLDEST reading.
  const orderedMetrics = useMemo(
    () => (sort.order === 'DESC' ? [...filteredMetrics].reverse() : filteredMetrics),
    [filteredMetrics, sort.order],
  )

  // S2 fix: a failed DEVICE fetch must surface here, not just a failed metrics
  // fetch. Previously only metricsError was exported — when GET /api/devices
  // failed, devices resolved to an empty list and the UI rendered "no device
  // selected" / "No data", indistinguishable from a pet that never had a CGM
  // sensor. A device-fetch error takes precedence over (is never masked by) a
  // simultaneous/null metrics error: if we don't know what device(s) to query,
  // a metrics error (or lack thereof) is not meaningful.
  const deviceError = isVirtual ? petDevicesError : singleDeviceError
  const error = deviceError ?? metricsError

  return {
    data: orderedMetrics,
    total: filteredMetrics.length,
    isLoading,
    error,
    isVirtualDevice: isVirtual,
    deviceIds: actualDeviceIds,
    devices: gatedDevices,
    hiddenRanges,
    isTruncated,
    truncatedBefore,
    perDeviceData,
  }
}
